import { useEffect, useState } from "react"
import type { FC } from "react"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  fetchPost,
  fetchComments,
  addComment,
  selectPost,
  selectComments,
  selectPostStatus,
  selectCommentStatus,
} from "@/features/postDetail/postDetailSlice"
import PostHeader from "./PostHeader"
import PostContent from "./PostContent"
import InteractionButtons from "./InteractionButtons"
import CommentList from "../Comment/CommentList"
import CommentInput from "../Comment/CommentInput"
import type { CommentResponse } from "@/types/domain"
import { ArrowLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"

import "@/styles/PostDetail.css"

type PostDetailViewProps = {
  postId: string
}

const PostDetailView: FC<PostDetailViewProps> = ({ postId }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const post = useAppSelector(selectPost)
  const comments = useAppSelector(selectComments)
  const status = useAppSelector(selectPostStatus)
  const commentStatus = useAppSelector(selectCommentStatus)

  const [replyingTo, setReplyingTo] = useState<CommentResponse | null>(null)

  useEffect(() => {
    void dispatch(fetchPost(postId))
    void dispatch(fetchComments({ postId, page: 0 }))
  }, [dispatch, postId])

  const handleReply = (comment: CommentResponse) => {
    setReplyingTo(comment)
    // 스크롤 등 UI 처리 필요 시 추가
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
  }

  const handleCancelReply = () => {
    setReplyingTo(null)
  }

  const handleSubmitComment = (content: string, parentId?: string) => {
    void dispatch(addComment({ postId, content, parentCommentId: parentId })).then(() => {
      setReplyingTo(null)
    })
  }

  if (status === "loading" && !post) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    )
  }

  if (status === "failed" || !post) {
    return (
      <div className="error-container">
        <div className="error-text">게시글을 불러올 수 없습니다.</div>
        <button
          onClick={() => { navigate(-1) }}
          className="back-btn"
        >
          뒤로 가기
        </button>
      </div>
    )
  }

  return (
    <div className="post-detail-container">
      {/* Header Navigation */}
      <div className="post-detail-header">
        <button onClick={() => { navigate(-1) }} className="header-back-btn">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <span className="header-title">{post.boardName}</span>
      </div>

      <div className="post-content-area">
        <PostHeader
          author={post.author}
          createdAt={post.createdAt}
          viewCount={post.viewCount}
        />

        <PostContent
          title={post.title}
          content={post.content}
          tags={post.tags}
          images={post.images}
          campingSites={post.campingSites}
        />

        <InteractionButtons
          postId={post.id}
          liked={post.liked}
          likeCount={post.likeCount}
        />

        <CommentList
          comments={comments}
          onReply={handleReply}
          loading={commentStatus === "loading"}
        />
      </div>

      <CommentInput
        onSubmit={handleSubmitComment}
        replyingTo={replyingTo}
        onCancelReply={handleCancelReply}
        loading={commentStatus === "loading"}
      />
    </div>
  )
}

export default PostDetailView
